import React, { useLayoutEffect, useMemo, useRef } from "react";

/** Optgroup label for axis options with `against_order: true`. */
export const STORE_VARIANT_OPTGROUP_AGAINST_ORDER = "Against Order";

/** @param {unknown} raw */
export function toNullableNumber(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/**
 * Stable identity for merging variant axis option lists (listing + project item payloads).
 * Prefer catalog parent id so switching store `product_id` keeps the merged option sets.
 */
export function resolveVariantAxesStableKey(p) {
  if (p == null || typeof p !== "object") return null;
  const cp = p.catalog_product_id ?? p.cp_id;
  if (cp != null && cp !== "") return String(cp);
  if (p.cp_code != null && String(p.cp_code).trim() !== "")
    return String(p.cp_code).trim();
  return toNullableNumber(p.id);
}

export function normalizeVariantAxisOptions(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.filter(
    (x) =>
      x &&
      typeof x === "object" &&
      toNullableNumber(x.product_id) != null &&
      String(x.label ?? "").trim() !== "",
  );
}

export function mergeVariantAxisOptionsByProductId(prev, incoming) {
  const map = new Map();
  for (const x of normalizeVariantAxisOptions(prev)) {
    map.set(toNullableNumber(x.product_id), x);
  }
  for (const x of normalizeVariantAxisOptions(incoming)) {
    map.set(toNullableNumber(x.product_id), x);
  }
  return Array.from(map.values());
}

/** Range rows reuse the same labels (Eco, Prime, …) with different `product_id` after H/W/D changes; dedupe by label so pills don’t stack duplicates. */
function normalizeRangeLabelKey(label) {
  return String(label ?? "").trim().toLowerCase();
}

function mergeRangeAxisOptionsByLabel(prev, incoming) {
  const prevByLabel = new Map();
  for (const x of normalizeVariantAxisOptions(prev)) {
    const k = normalizeRangeLabelKey(x.label);
    if (k) prevByLabel.set(k, x);
  }
  const out = [];
  const seen = new Set();
  for (const x of normalizeVariantAxisOptions(incoming)) {
    const k = normalizeRangeLabelKey(x.label);
    if (!k) continue;
    seen.add(k);
    out.push(x);
  }
  for (const [k, x] of prevByLabel) {
    if (!seen.has(k)) out.push(x);
  }
  return out;
}

export function mergeNewVariantAxesSnapshot(prev, incoming) {
  if (!prev) return incoming;
  const ps =
    prev.size && typeof prev.size === "object" && !Array.isArray(prev.size)
      ? prev.size
      : {};
  const ns =
    incoming.size &&
    typeof incoming.size === "object" &&
    !Array.isArray(incoming.size)
      ? incoming.size
      : {};
  const size = {};
  for (const axis of ["height", "width", "depth"]) {
    size[axis] = mergeVariantAxisOptionsByProductId(ps[axis], ns[axis]);
  }
  const range = mergeRangeAxisOptionsByLabel(prev.range, incoming.range);
  const lBox = incoming.l_box === true || prev.l_box === true;

  return { l_box: lBox, size, range };
}

export function parseNewVariantAxesFromProduct(p) {
  const v = p?.variants;
  if (!v || typeof v !== "object") {
    return {
      l_box: false,
      size: { height: [], width: [], depth: [] },
      range: [],
    };
  }
  const sz =
    v.size && typeof v.size === "object" && !Array.isArray(v.size)
      ? v.size
      : {};
  return {
    l_box: v.l_box === true,
    size: {
      height: normalizeVariantAxisOptions(sz.height),
      width: normalizeVariantAxisOptions(sz.width),
      depth: normalizeVariantAxisOptions(sz.depth),
    },
    range: normalizeVariantAxisOptions(v.range),
  };
}

export function resolveVariantSizeAxes(axes) {
  const lBox = axes?.l_box === true;
  const sz =
    axes?.size && typeof axes.size === "object" && !Array.isArray(axes.size)
      ? axes.size
      : {};

  return {
    lBox,
    heightOpts: normalizeVariantAxisOptions(sz.height),
    widthOpts: normalizeVariantAxisOptions(sz.width),
    depthOpts: normalizeVariantAxisOptions(sz.depth),
  };
}

export function pickVariantSizeProductIds(axes, listProductId) {
  const { heightOpts, widthOpts, depthOpts } = resolveVariantSizeAxes(axes);

  return {
    heightPid: pickAxisProductId(heightOpts, listProductId),
    widthPid: pickAxisProductId(widthOpts, listProductId),
    depthPid: pickAxisProductId(depthOpts, listProductId),
  };
}

/**
 * Keep the largest option sets per axis when the API returns pruned `variants` after selection.
 */
export function useStableNewVariantAxes(product) {
  const stableKey = resolveVariantAxesStableKey(product);
  const incoming = useMemo(
    () => parseNewVariantAxesFromProduct(product),
    [product],
  );
  const cacheRef = useRef({ key: null, snapshot: null });

  useLayoutEffect(() => {
    if (stableKey == null) {
      cacheRef.current = { key: null, snapshot: incoming };
      return;
    }
    if (cacheRef.current.key !== stableKey) {
      cacheRef.current = { key: stableKey, snapshot: incoming };
      return;
    }
    const merged = mergeNewVariantAxesSnapshot(
      cacheRef.current.snapshot,
      incoming,
    );
    const prevCount =
      (cacheRef.current.snapshot?.range?.length ?? 0) +
      ["height", "width", "depth"].reduce(
        (n, a) => n + (cacheRef.current.snapshot?.size?.[a]?.length ?? 0),
        0,
      );
    const nextCount =
      (merged.range?.length ?? 0) +
      ["height", "width", "depth"].reduce(
        (n, a) => n + (merged.size?.[a]?.length ?? 0),
        0,
      );
    if (nextCount >= prevCount) {
      cacheRef.current = { key: stableKey, snapshot: merged };
    }
  }, [stableKey, incoming]);

  if (stableKey == null) return incoming;
  if (cacheRef.current.key !== stableKey) return incoming;
  return cacheRef.current.snapshot || incoming;
}

export function pickAxisProductId(axisList, listProductId) {
  if (!Array.isArray(axisList) || axisList.length === 0) return null;
  const byCurrent = axisList.find((o) => o?.current === true);
  if (byCurrent && toNullableNumber(byCurrent.product_id) != null) {
    return toNullableNumber(byCurrent.product_id);
  }
  const byId = axisList.find(
    (o) => toNullableNumber(o?.product_id) === listProductId,
  );
  if (byId && toNullableNumber(byId.product_id) != null) {
    return toNullableNumber(byId.product_id);
  }
  return toNullableNumber(axisList[0]?.product_id);
}

/** @param {unknown} a @param {unknown} b */
export function compareStoreVariantLabelsAsc(a, b) {
  const sa = String(a ?? "");
  const sb = String(b ?? "");
  const ma = sa.match(/-?\d+(?:\.\d+)?/);
  const mb = sb.match(/-?\d+(?:\.\d+)?/);
  const na = ma ? Number(ma[0]) : null;
  const nb = mb ? Number(mb[0]) : null;
  if (na != null && nb != null && na !== nb) return na - nb;
  return sa.localeCompare(sb, undefined, { numeric: true });
}

/**
 * Split new-shape axis options: standard first, then `Against Order` (ascending by label).
 * @param {unknown} axisList
 */
export function partitionVariantAxisOptionsForSelect(axisList) {
  const normalized = normalizeVariantAxisOptions(axisList);
  const standard = [];
  const againstOrder = [];
  for (const opt of normalized) {
    if (opt.against_order === true) againstOrder.push(opt);
    else standard.push(opt);
  }
  againstOrder.sort((x, y) =>
    compareStoreVariantLabelsAsc(x.label, y.label),
  );
  return { standard, againstOrder };
}

/** @param {unknown} axisList @param {string} keyPrefix */
export function StoreNewVariantAxisSelectOptions({ axisList, keyPrefix }) {
  const { standard, againstOrder } = useMemo(
    () => partitionVariantAxisOptionsForSelect(axisList),
    [axisList],
  );
  return (
    <>
      {standard.map((opt) => (
        <option
          key={`${keyPrefix}-${opt.product_id}`}
          value={String(opt.product_id)}
        >
          {String(opt.label ?? "").trim()}
        </option>
      ))}
      {againstOrder.length > 0 ? (
        <optgroup label={STORE_VARIANT_OPTGROUP_AGAINST_ORDER}>
          {againstOrder.map((opt) => (
            <option
              key={`${keyPrefix}-${opt.product_id}`}
              value={String(opt.product_id)}
            >
              {String(opt.label ?? "").trim()}
            </option>
          ))}
        </optgroup>
      ) : null}
    </>
  );
}

export function StoreProductVariantDimensionPickers({
  axes,
  listProductId,
  onPickProductId,
  disabled = false,
  keyPrefix = "dim",
  className = "store-products-row__dims-row",
}) {
  const { heightOpts, widthOpts, depthOpts } = resolveVariantSizeAxes(axes);
  const { heightPid, widthPid, depthPid } = pickVariantSizeProductIds(
    axes,
    listProductId,
  );

  const handlePid = (nextPid) => {
    const nid = toNullableNumber(nextPid);
    if (nid == null || nid === listProductId) return;
    onPickProductId(nid);
  };

  return (
    <div className={className}>
      {heightOpts.length > 0 ? (
        <div className="store-products-row__dim-cell">
          <span className="store-products-row__label">H</span>
          <select
            className="form-select form-select-sm"
            value={heightPid != null ? String(heightPid) : ""}
            disabled={disabled}
            onChange={(e) => handlePid(e.target.value)}
          >
            <StoreNewVariantAxisSelectOptions
              axisList={heightOpts}
              keyPrefix={`${keyPrefix}-h`}
            />
          </select>
        </div>
      ) : null}
      {widthOpts.length > 0 ? (
        <div className="store-products-row__dim-cell">
          <span className="store-products-row__label">W</span>
          <select
            className="form-select form-select-sm"
            value={widthPid != null ? String(widthPid) : ""}
            disabled={disabled}
            onChange={(e) => handlePid(e.target.value)}
          >
            <StoreNewVariantAxisSelectOptions
              axisList={widthOpts}
              keyPrefix={`${keyPrefix}-w`}
            />
          </select>
        </div>
      ) : null}
      {depthOpts.length > 0 ? (
        <div className="store-products-row__dim-cell">
          <span className="store-products-row__label">D</span>
          <select
            className="form-select form-select-sm"
            value={depthPid != null ? String(depthPid) : ""}
            disabled={disabled}
            onChange={(e) => handlePid(e.target.value)}
          >
            <StoreNewVariantAxisSelectOptions
              axisList={depthOpts}
              keyPrefix={`${keyPrefix}-d`}
            />
          </select>
        </div>
      ) : null}
    </div>
  );
}
